import React from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useParams } from "react-router-dom-v5-compat";

import {
  LinkButton,
  PrimaryAction,
  RecordFeatureView,
  TitleBar,
} from "./Atoms";
import { ItemBoxContainer, ItemCountdown } from "./Pages/Sections/Section";
import { useVenue } from "./VenueContext";
import { getPromotions } from "./menuSelectors";
import { useFetchLiveItemCountdown, useFetchMenu } from "./queries";
import type { Item, Menu } from "./schema";
import { getVenueHasMobileOrdering } from "./venueSelectors";

const DefaultSection = ({ menu }: { menu: Menu }) => {
  const { t } = useTranslation("onsite");
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const firstSection = menu.sections[0];
  return (
    <>
      <div className="space-y-24">
        <h2 className="text-xl font-bold md:text-center md:text-4xl">
          {firstSection.name}
        </h2>

        <div className="grid grid-cols-1 gap-x-48 gap-y-12 xl:grid-cols-2">
          {firstSection.items.map((item, index) => (
            <Link
              className="rounded-venue-brand"
              key={index}
              to={`/${firstSection.uuid}/${item.uuid}/new`}
            >
              <ItemBoxContainer item={item} key={`${index}-${item.uuid}`} />
            </Link>
          ))}
        </div>
      </div>
      <PrimaryAction>
        <LinkButton primary to="your-order/pending">
          {orderingEnabled
            ? t("mainPage.yourOrder")
            : t("mainPage.yourWishlist")}
        </LinkButton>
      </PrimaryAction>
    </>
  );
};

const FavouriteItem = ({ item }: { item: Item }) => {
  const { t } = useTranslation("onsite");
  const itemCountdownQuery = useFetchLiveItemCountdown(item.uuid);
  const soldOut =
    itemCountdownQuery.data && itemCountdownQuery.data.remainingCount <= 0;
  return (
    <Link
      to={`promotions/${item.uuid}`}
      className={classNames(
        "relative rounded-venue-brand bg-venue-tertiary shadow-venue-brand-style",
      )}
    >
      <div className="aspect-h-10 aspect-w-7">
        {item.heroImageUrl && (
          <img
            className={classNames("rounded-t-venue-brand object-cover", {
              "opacity-50 grayscale": soldOut,
            })}
            src={item.heroImageUrl}
          />
        )}
      </div>
      <div className="absolute right-0 top-0 p-8">
        <ItemCountdown count={itemCountdownQuery.data?.remainingCount} />
      </div>
      <div className="space-y-4 p-12">
        <div className="font-lg flex justify-between text-xl">
          <div>{item.name}</div>
          <div>{item.salesPrice}</div>
        </div>
        <div className="text-xs">{item.marketingBlurb}</div>
      </div>
    </Link>
  );
};

const Promotions = () => {
  const menu = useFetchMenu();
  const { t } = useTranslation("onsite");
  const orderingEnabled = getVenueHasMobileOrdering(useVenue());
  const promotions = getPromotions(menu);

  if (promotions.length == 0) {
    return <DefaultSection menu={menu} />;
  }
  return (
    <>
      <div className="space-y-24">
        <h2 className="text-xl font-bold md:text-center md:text-4xl">
          {t("promotions.title")}
        </h2>

        {false ? (
          <div className="grid grid-cols-1 gap-x-48 gap-y-12 xl:grid-cols-2">
            {promotions.map((promotedItem, index) => (
              <Link
                className="rounded-venue-brand"
                key={index}
                to={`promotions/${promotedItem.uuid}`}
              >
                <ItemBoxContainer
                  item={{
                    ...promotedItem,
                    detail: promotedItem.marketingBlurb,
                  }}
                />
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-x-12 gap-y-32 sm:grid-cols-3 sm:gap-x-24">
            {promotions.map((promotedItem, index) => (
              <FavouriteItem item={promotedItem} key={promotedItem.uuid} />
            ))}
          </div>
        )}
      </div>
      <PrimaryAction>
        <LinkButton primary to="your-order/pending">
          {orderingEnabled
            ? t("mainPage.yourOrder")
            : t("mainPage.yourWishlist")}
        </LinkButton>
      </PrimaryAction>
    </>
  );
};

export const Promotion = () => {
  const menu = useFetchMenu();
  const { itemUuid } = useParams();
  const promotedItem = menu.sections
    .flatMap((i) => i.items)
    .find((p) => p.uuid == itemUuid);
  if (!promotedItem) {
    return <div>No Promotion </div>;
  }
  return (
    <RecordFeatureView featureName="Online / Promotion">
      <div>
        <div className="hidden md:block">
          <TitleBar>Promotions</TitleBar>
        </div>
        <Outlet />
      </div>
    </RecordFeatureView>
  );
};

export default Promotions;
